import { useContext, useEffect, useMemo, useRef } from 'react';
import { FooterDisclaimer } from 'organisms';
import { NavigateConst, PROXY_AUTHORIZATION_ERROR_URL } from 'common';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '@sentara-health/optimahealth_web';
import { useVerifyProxyAuthorizationLink } from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from 'context';
import ProxyHeader from './ProxyHeader';

const ProxyRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setProxyAuthorizationLinkData } = useContext(GlobalContext);
  const { verifyProxyAuthorizationLink } = useVerifyProxyAuthorizationLink();

  const processedRequestKeysRef = useRef(new Set<string>());
  const initialSearchRef = useRef(location.search);

  const searchParams = useMemo(
    () => new URLSearchParams(initialSearchRef.current),
    []
  );
  const correlationId =
    searchParams.get('CorrelationId') ?? searchParams.get('correlationId') ?? '';
  const forgerockUuid =
    searchParams.get('forgerockuuid') ?? searchParams.get('forgerockUuid') ?? '';
  const token = searchParams.get('token') ?? '';
  const requestKey = `${correlationId}|${forgerockUuid}|${token}`;

  useEffect(() => {
    if (!location.search) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
      },
      {
        replace: true,
        state: location.state,
      }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const navigateToErrorPage = (message: string) => {
      navigate(
        {
          pathname: PROXY_AUTHORIZATION_ERROR_URL,
        },
        {
          replace: true,
          state: {
            errorMessage: message,
          },
        }
      );
    };

    const verifyAuthorizationLink = async () => {
      if (processedRequestKeysRef.current.has(requestKey)) {
        return;
      }

      processedRequestKeysRef.current.add(requestKey);

      if (!token || !correlationId || !forgerockUuid) {
        navigateToErrorPage('Unable to process this authorization link.');
        return;
      }

      try {
        const response = await verifyProxyAuthorizationLink({ token });
        const typedResponse = (response ?? {}) as {
          status?: number;
          data?: { status?: number; detail?: string };
          error?: string | { detail?: string; title?: string };
          errorCode?: string;
        };

        const status = typedResponse.status ?? typedResponse.data?.status;
        const errorDetails =
          typeof typedResponse.error === 'object' && typedResponse.error
            ? typedResponse.error
            : undefined;

        const isSuccess =
          typeof status === 'number' &&
          status >= 200 &&
          status < 300 &&
          !typedResponse.error &&
          !typedResponse.errorCode;

        if (!isSuccess) {
          navigateToErrorPage(
            (typeof typedResponse.error === 'string' && typedResponse.error) ||
              errorDetails?.detail ||
              errorDetails?.title ||
              typedResponse.data?.detail ||
              typedResponse.errorCode ||
              'Unable to verify your authorization link.'
          );
          return;
        }

        setProxyAuthorizationLinkData({
          correlationId,
          forgerockUuid,
          token,
        });

        navigate(
          {
            pathname: NavigateConst.drAuthorizationChooser,
          },
          { replace: true }
        );
      } catch (error) {
        const errorMessage =
          typeof error === 'string'
            ? error
            : error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? '')
            : '';

        navigateToErrorPage(errorMessage || 'Unable to verify your authorization link.');
      }
    };

    void verifyAuthorizationLink();
  }, [
    correlationId,
    forgerockUuid,
    navigate,
    requestKey,
    setProxyAuthorizationLinkData,
    token,
    verifyProxyAuthorizationLink,
  ]);

  return (
    <div className="d-flex flex-column h-100">
      <ProxyHeader />
      <div className="container-fluid" aria-live="polite">
        <div
          id="main-content"
          className='container-lg px-main'
        >
          <div data-testid="CSRProxy">
            <div className="row justify-content-center my-5">
              <div className="col-lg-6 col-md-8 col-sm-12">
                <div className="text-center mb-5">
                  <div
                    data-testid="proxy-register-loader"
                    className="d-flex justify-content-center my-4"
                  >
                    <Loader />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer section */}
      <footer className="container-lg mt-auto">
        {/* Component to display the footer disclaimer */}
        <FooterDisclaimer />
      </footer>
    </div>
  );
};

export default ProxyRegister;
